import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import RichTextEditor from './RichTextEditor';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { jobAPI } from '../../services/api';
import ApiService from '../../services/api';
import { getApiUrl } from '../../config/api';
import './JobPostingForm.css';

const JobPostingForm = ({ isOpen, onClose, onSuccess, jobToEdit = null }) => {
  const { user } = useAuth();
  const { companies } = useCompany();
  const [mounted, setMounted] = useState(false);
  
  // Initialize form data based on whether we're editing or creating
  const getInitialFormData = () => {
    if (jobToEdit) {
      // Pre-fill form with job data for editing
      // Reconstruct salary_range from salary_min and salary_max
      let salaryRange = '';
      if (jobToEdit.salary_min && jobToEdit.salary_max) {
        salaryRange = `${Math.round(jobToEdit.salary_min)}-${Math.round(jobToEdit.salary_max)}`;
      } else if (jobToEdit.salary_min) {
        salaryRange = `${Math.round(jobToEdit.salary_min)}+`;
      }
      
      return {
        title: jobToEdit.title || '',
        description: jobToEdit.description || '',
        requirements: jobToEdit.requirements || '',
        job_type: jobToEdit.job_type || 'full-time',
        work_type: jobToEdit.work_type || 'remote',
        salary_range: salaryRange,
        salary_currency: jobToEdit.salary_currency || 'USD',
        salary_period: jobToEdit.salary_period || 'yearly',
        experience_level: jobToEdit.experience_level || '',
        category_id: jobToEdit.category_id || '',
        skills_required: Array.isArray(jobToEdit.skills_required) ? jobToEdit.skills_required : [],
        application_deadline: jobToEdit.application_deadline 
          ? (jobToEdit.application_deadline.split('T')[0] || '')
          : '',
        timezone: jobToEdit.timezone || user?.timezone || '',
        status: jobToEdit.status || 'draft',
        company_id: jobToEdit.company_id || null,
        meta_fields: Array.isArray(jobToEdit.meta_fields) ? jobToEdit.meta_fields : [],
      };
    }
    
    // Default form data for new job
    return {
      title: '',
      description: '',
      requirements: '',
      job_type: 'full-time',
      work_type: 'remote',
      salary_range: '',
      salary_currency: 'USD',
      salary_period: 'yearly',
      experience_level: '',
      category_id: '',
      skills_required: [],
      application_deadline: '',
      timezone: user?.timezone || '',
      status: 'draft',
      company_id: null,
      meta_fields: [],
    };
  };
  
  const [formData, setFormData] = useState(getInitialFormData());
  const [skillsInput, setSkillsInput] = useState('');
  const [availableSkills, setAvailableSkills] = useState([]);
  const [filteredSkills, setFilteredSkills] = useState([]);
  const [showSkillsDropdown, setShowSkillsDropdown] = useState(false);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const skillsDropdownRef = useRef(null);
  const skillsInputRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  
  // Combined loading state for disabling form fields
  const isSubmitting = isSavingDraft || isPublishing;
  const totalSteps = 3;
  const [timezoneOptions, setTimezoneOptions] = useState([]);

  // Helper function to strip HTML tags for character counting
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };


  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Reset form when jobToEdit changes or when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData(getInitialFormData());
      setCurrentStep(1);
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, jobToEdit?.id]);

  useEffect(() => {
    // Fetch timezone options
    const fetchTimezones = async () => {
      try {
        const response = await fetch(getApiUrl('/timezones'));
        if (!response.ok) {
          throw new Error('Failed to fetch timezones');
        }
        const data = await response.json();
        if (data.success && data.data && Array.isArray(data.data)) {
          setTimezoneOptions(data.data);
        } else {
          // Fallback to common timezones if API fails
          setTimezoneOptions([
            { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
            { value: 'America/New_York', label: 'EST/EDT (Eastern Time - US & Canada)' },
            { value: 'America/Chicago', label: 'CST/CDT (Central Time - US & Canada)' },
            { value: 'America/Denver', label: 'MST/MDT (Mountain Time - US & Canada)' },
            { value: 'America/Los_Angeles', label: 'PST/PDT (Pacific Time - US & Canada)' },
            { value: 'Europe/London', label: 'GMT/BST (London, UK)' },
            { value: 'Europe/Paris', label: 'CET/CEST (Paris, France)' },
            { value: 'Asia/Dubai', label: 'GST (Dubai, UAE)' },
            { value: 'Asia/Kolkata', label: 'IST (India Standard Time)' },
            { value: 'Asia/Singapore', label: 'SGT (Singapore)' },
            { value: 'Asia/Tokyo', label: 'JST (Japan Standard Time)' },
            { value: 'Australia/Sydney', label: 'AEST/AEDT (Sydney, Australia)' },
          ]);
        }
      } catch (error) {
        console.error('Error fetching timezones:', error);
        // Fallback to common timezones on error
        setTimezoneOptions([
          { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
          { value: 'America/New_York', label: 'EST/EDT (Eastern Time - US & Canada)' },
          { value: 'America/Chicago', label: 'CST/CDT (Central Time - US & Canada)' },
          { value: 'America/Denver', label: 'MST/MDT (Mountain Time - US & Canada)' },
          { value: 'America/Los_Angeles', label: 'PST/PDT (Pacific Time - US & Canada)' },
          { value: 'Europe/London', label: 'GMT/BST (London, UK)' },
          { value: 'Europe/Paris', label: 'CET/CEST (Paris, France)' },
          { value: 'Asia/Dubai', label: 'GST (Dubai, UAE)' },
          { value: 'Asia/Kolkata', label: 'IST (India Standard Time)' },
          { value: 'Asia/Singapore', label: 'SGT (Singapore)' },
          { value: 'Asia/Tokyo', label: 'JST (Japan Standard Time)' },
          { value: 'Australia/Sydney', label: 'AEST/AEDT (Sydney, Australia)' },
        ]);
      }
    };
    if (isOpen) {
      fetchTimezones();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Fetch available skills when step 3 is accessed
  useEffect(() => {
    if (isOpen && currentStep === 3 && availableSkills.length === 0) {
      fetchAvailableSkills();
    }
  }, [isOpen, currentStep]);

  // Fetch available skills from system
  const fetchAvailableSkills = async () => {
    setSkillsLoading(true);
    try {
      const response = await ApiService.get('/skills', {
        params: { per_page: 1000 } // Get a large number of skills
      });
      
      if (response.success) {
        // Handle different response structures
        let allSkills = [];
        
        if (response.data) {
          // Check if it's a paginated response
          if (response.data.data && Array.isArray(response.data.data)) {
            allSkills = response.data.data;
          } 
          // Check if it's a direct array
          else if (Array.isArray(response.data)) {
            allSkills = response.data;
          }
          // Check if there's a nested data structure
          else if (response.data.data && Array.isArray(response.data.data)) {
            allSkills = response.data.data;
          }
        }
        
        setAvailableSkills(allSkills);
        setFilteredSkills(allSkills);
      }
    } catch (error) {
      console.error('Error fetching skills:', error);
      setErrors(prev => ({
        ...prev,
        skills_required: 'Failed to load available skills. Please try again.'
      }));
    } finally {
      setSkillsLoading(false);
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        skillsDropdownRef.current &&
        !skillsDropdownRef.current.contains(event.target) &&
        skillsInputRef.current &&
        !skillsInputRef.current.contains(event.target)
      ) {
        setShowSkillsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!isOpen || !mounted) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSkillsInput = (e) => {
    const value = e.target.value;
    setSkillsInput(value);
    
    // Filter available skills based on input
    if (value.trim()) {
      const filtered = availableSkills.filter(skill =>
        skill.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSkills(filtered);
      setShowSkillsDropdown(true);
    } else {
      setFilteredSkills(availableSkills);
      setShowSkillsDropdown(true);
    }
  };

  const handleSkillSelect = (skill) => {
    const skillName = skill.name || skill;
    // Check if skill is already added
    if (!formData.skills_required.includes(skillName)) {
      setFormData(prev => ({
        ...prev,
        skills_required: [...prev.skills_required, skillName]
      }));
    }
    setSkillsInput('');
    setShowSkillsDropdown(false);
  };

  const handleAddSkill = (e) => {
    if (e.key === 'Enter' && skillsInput.trim()) {
      e.preventDefault();
      // Find matching skill from available skills
      const matchingSkill = availableSkills.find(skill =>
        skill.name.toLowerCase() === skillsInput.trim().toLowerCase()
      );
      
      if (matchingSkill) {
        handleSkillSelect(matchingSkill);
      } else {
        // If no exact match, show error or use first filtered result
        if (filteredSkills.length > 0) {
          handleSkillSelect(filteredSkills[0]);
        } else {
          setErrors(prev => ({
            ...prev,
            skills_required: 'Please select a skill from the available options'
          }));
        }
      }
    } else if (e.key === 'Escape') {
      setShowSkillsDropdown(false);
    }
  };

  const removeSkill = (skillToRemove) => {
    setFormData(prev => ({
      ...prev,
      skills_required: prev.skills_required.filter(skill => skill !== skillToRemove)
    }));
    if (errors.skills_required) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.skills_required;
        return newErrors;
      });
    }
  };

  const handleSkillsInputFocus = () => {
    if (availableSkills.length > 0) {
      setShowSkillsDropdown(true);
      setFilteredSkills(availableSkills);
    }
  };

  // Meta fields (questions) handlers
  const addMetaField = () => {
    setFormData(prev => ({
      ...prev,
      meta_fields: [
        ...prev.meta_fields,
        {
          question: '',
          type: 'text',
          required: false,
          options: [],
        }
      ]
    }));
  };

  const removeMetaField = (index) => {
    setFormData(prev => ({
      ...prev,
      meta_fields: prev.meta_fields.filter((_, i) => i !== index)
    }));
  };

  const updateMetaField = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.meta_fields];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      
      // If type changes to text/textarea/rich_text, clear options
      if (field === 'type' && ['text', 'textarea', 'rich_text'].includes(value)) {
        updated[index].options = [];
      }
      
      // If type changes to dropdown/checkbox/radio, initialize options if empty
      if (field === 'type' && ['dropdown', 'checkbox', 'radio'].includes(value) && updated[index].options.length === 0) {
        updated[index].options = [''];
      }
      
      return {
        ...prev,
        meta_fields: updated
      };
    });
  };

  const addMetaFieldOption = (metaFieldIndex) => {
    setFormData(prev => {
      const updated = [...prev.meta_fields];
      updated[metaFieldIndex] = {
        ...updated[metaFieldIndex],
        options: [...updated[metaFieldIndex].options, '']
      };
      return {
        ...prev,
        meta_fields: updated
      };
    });
  };

  const removeMetaFieldOption = (metaFieldIndex, optionIndex) => {
    setFormData(prev => {
      const updated = [...prev.meta_fields];
      updated[metaFieldIndex] = {
        ...updated[metaFieldIndex],
        options: updated[metaFieldIndex].options.filter((_, i) => i !== optionIndex)
      };
      return {
        ...prev,
        meta_fields: updated
      };
    });
  };

  const updateMetaFieldOption = (metaFieldIndex, optionIndex, value) => {
    setFormData(prev => {
      const updated = [...prev.meta_fields];
      updated[metaFieldIndex].options[optionIndex] = value;
      return {
        ...prev,
        meta_fields: updated
      };
    });
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.title.trim()) {
        newErrors.title = 'Job title is required';
      }
      const descriptionText = stripHtml(formData.description);
      if (!descriptionText.trim()) {
        newErrors.description = 'Job description is required';
      } else if (descriptionText.length < 50) {
        newErrors.description = 'Description must be at least 50 characters';
      }
      if (!formData.job_type) {
        newErrors.job_type = 'Job type is required';
      }
      if (!formData.timezone) {
        newErrors.timezone = 'Timezone is required';
      }
      // Work type is always remote, no validation needed
    }
    
    if (step === 2) {
      // Salary range validation if needed
      // No validation needed as ranges are predefined
    }

    if (step === 3) {
      // Additional info step - no required fields, all optional
      // No validation needed for step 3
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const createPayload = () => {
    // Parse salary range if provided
    let salary_min = null;
    let salary_max = null;
    if (formData.salary_range) {
      if (formData.salary_range.includes('+')) {
        // Handle ranges like "300000+"
        salary_min = parseFloat(formData.salary_range.replace('+', ''));
        salary_max = null; // No maximum for "+" ranges
      } else {
        // Handle ranges like "80000-100000"
        const [min, max] = formData.salary_range.split('-').map(val => parseFloat(val.trim()));
        salary_min = min;
        salary_max = max;
      }
    }

    const payload = {
      ...formData,
      company_id: formData.company_id || null,
      salary_min: salary_min,
      salary_max: salary_max,
      category_id: formData.category_id || null,
      application_deadline: formData.application_deadline || null,
      experience_level: formData.experience_level || null,
    };
    
    // Remove salary_range from payload as it's not a database field
    delete payload.salary_range;
    
    return payload;
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      requirements: '',
      job_type: 'full-time',
      work_type: 'remote',
      salary_range: '',
      salary_currency: 'USD',
      salary_period: 'yearly',
      experience_level: '',
      category_id: '',
      skills_required: [],
      application_deadline: '',
      timezone: user?.timezone || '',
      status: 'draft',
      company_id: null,
      meta_fields: [],
    });
    setCurrentStep(1);
    setErrors({});
  };

  const handleSaveDraft = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only validate step 1 (title and description) for draft
    if (!formData.title.trim()) {
      setErrors({ title: 'Job title is required' });
      setCurrentStep(1);
      return;
    }

    setIsSavingDraft(true);
    
    try {
      const payload = createPayload();
      payload.status = 'draft'; // Ensure it's saved as draft

      const result = jobToEdit 
        ? await jobAPI.update(jobToEdit.id, payload)
        : await jobAPI.create(payload);
      
      if (result.success) {
        if (onSuccess) {
          onSuccess(result.data);
        }
        onClose();
        resetForm();
      } else {
        if (result.errors) {
          setErrors(result.errors);
        } else {
          setErrors({ general: result.message || 'Failed to save draft' });
        }
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      setErrors({ general: error.message || 'An error occurred while saving the draft' });
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handlePublish = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Validate all steps before publishing
    if (!validateStep(1) || !validateStep(2) || !validateStep(3)) {
      // If validation fails, go to the first step with errors
      if (!validateStep(1)) {
        setCurrentStep(1);
      } else if (!validateStep(2)) {
        setCurrentStep(2);
      }
      return;
    }

    setIsPublishing(true);
    
    try {
      const payload = createPayload();
      payload.status = 'published'; // Set to published
      payload.is_active = true; // Ensure it's active when published

      const result = jobToEdit 
        ? await jobAPI.update(jobToEdit.id, payload)
        : await jobAPI.create(payload);
      
      if (result.success) {
        if (onSuccess) {
          onSuccess(result.data);
        }
        onClose();
        resetForm();
      } else {
        if (result.errors) {
          setErrors(result.errors);
        } else {
          setErrors({ general: result.message || 'Failed to publish job posting' });
        }
      }
    } catch (error) {
      console.error('Error publishing job:', error);
      setErrors({ general: error.message || 'An error occurred while publishing the job' });
    } finally {
      setIsPublishing(false);
    }
  };

  const modalContent = (
    <div className="job-posting-modal-overlay" onClick={onClose}>
      <div className="job-posting-modal" onClick={(e) => e.stopPropagation()}>
        <div className="job-posting-header">
          <div className="header-left">
            <h2>{jobToEdit ? 'Edit Job' : 'Post a Job'}</h2>
            <p className="header-subtitle">{jobToEdit ? 'Update your job posting details' : 'Reach qualified candidates and build your team'}</p>
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="job-posting-progress">
          <div className="progress-steps">
            {[1, 2, 3].map((step) => (
              <div key={step} className={`progress-step ${currentStep >= step ? 'active' : ''}`}>
                <div className="step-number">{step}</div>
                <div className="step-label">
                  {step === 1 && 'Job Details'}
                  {step === 2 && 'Compensation'}
                  {step === 3 && 'Additional Info'}
                </div>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={(e) => e.preventDefault()} className="job-posting-form">
          {errors.general && (
            <div className="error-message general-error">
              {errors.general}
            </div>
          )}

          {/* Step 1: Job Details */}
          {currentStep === 1 && (
            <div className="form-step">
              <div className="step-content">
                <h3>Tell us about the position</h3>
                
                <div className="form-group">
                  <label htmlFor="title">Job Title <span className="required">*</span></label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className={errors.title ? 'error' : ''}
                    placeholder="e.g., Senior Software Engineer"
                    disabled={isSubmitting}
                  />
                  {errors.title && <span className="error-text">{errors.title}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="job_type">Job Type <span className="required">*</span></label>
                  <select
                    id="job_type"
                    name="job_type"
                    value={formData.job_type}
                    onChange={handleInputChange}
                    className={errors.job_type ? 'error' : ''}
                    disabled={isSubmitting}
                  >
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="freelance">Freelance</option>
                    <option value="internship">Internship</option>
                  </select>
                  {errors.job_type && <span className="error-text">{errors.job_type}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="description">Job Description <span className="required">*</span></label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(content) => setFormData(prev => ({ ...prev, description: content }))}
                    placeholder="Describe the role, responsibilities, and what makes this opportunity great..."
                    disabled={isSubmitting}
                    error={errors.description}
                  />
                  <div className="char-count">
                    {stripHtml(formData.description).length} characters {stripHtml(formData.description).length < 50 && '(minimum 50 required)'}
                  </div>
                  {errors.description && <span className="error-text">{errors.description}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="requirements">Requirements (Optional)</label>
                  <RichTextEditor
                    value={formData.requirements}
                    onChange={(content) => setFormData(prev => ({ ...prev, requirements: content }))}
                    placeholder="List specific requirements, qualifications, or skills needed..."
                    disabled={isSubmitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="timezone">Job Timezone <span className="required">*</span></label>
                  <select
                    id="timezone"
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleInputChange}
                    className={errors.timezone ? 'error' : ''}
                    disabled={isSubmitting}
                  >
                    <option value="">Select timezone</option>
                    {timezoneOptions.length > 0 ? (
                      timezoneOptions.map((tz) => (
                        <option key={tz.value || tz.identifier} value={tz.value || tz.identifier}>
                          {tz.label || tz.display_name || tz.name}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="UTC">UTC (Coordinated Universal Time)</option>
                        <option value="America/New_York">EST/EDT (Eastern Time - US & Canada)</option>
                        <option value="America/Chicago">CST/CDT (Central Time - US & Canada)</option>
                        <option value="America/Denver">MST/MDT (Mountain Time - US & Canada)</option>
                        <option value="America/Los_Angeles">PST/PDT (Pacific Time - US & Canada)</option>
                        <option value="Europe/London">GMT/BST (London, UK)</option>
                        <option value="Europe/Paris">CET/CEST (Paris, France)</option>
                        <option value="Asia/Dubai">GST (Dubai, UAE)</option>
                        <option value="Asia/Kolkata">IST (India Standard Time)</option>
                        <option value="Asia/Singapore">SGT (Singapore)</option>
                        <option value="Asia/Tokyo">JST (Japan Standard Time)</option>
                        <option value="Australia/Sydney">AEST/AEDT (Sydney, Australia)</option>
                      </>
                    )}
                  </select>
                  <small style={{color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                    Select the primary timezone for this remote position
                  </small>
                  {errors.timezone && <span className="error-text">{errors.timezone}</span>}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Compensation */}
          {currentStep === 2 && (
            <div className="form-step">
              <div className="step-content">
                <h3>Compensation & Benefits</h3>
                
                <div className="form-group">
                  <label htmlFor="salary_range">Salary Range</label>
                  <select
                    id="salary_range"
                    name="salary_range"
                    value={formData.salary_range}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  >
                    <option value="">Select salary range</option>
                    <option value="0-20000">$0 - $20,000</option>
                    <option value="20000-40000">$20,000 - $40,000</option>
                    <option value="40000-60000">$40,000 - $60,000</option>
                    <option value="60000-80000">$60,000 - $80,000</option>
                    <option value="80000-100000">$80,000 - $100,000</option>
                    <option value="100000-120000">$100,000 - $120,000</option>
                    <option value="120000-150000">$120,000 - $150,000</option>
                    <option value="150000-200000">$150,000 - $200,000</option>
                    <option value="200000-250000">$200,000 - $250,000</option>
                    <option value="250000-300000">$250,000 - $300,000</option>
                    <option value="300000+">$300,000+</option>
                  </select>
                  <small style={{color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                    Select the salary range for this position
                  </small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="salary_currency">Currency</label>
                    <select
                      id="salary_currency"
                      name="salary_currency"
                      value={formData.salary_currency}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                    >
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                      <option value="GBP">GBP (£)</option>
                      <option value="INR">INR (₹)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="salary_period">Period</label>
                    <select
                      id="salary_period"
                      name="salary_period"
                      value={formData.salary_period}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                    >
                      <option value="hourly">Per Hour</option>
                      <option value="daily">Per Day</option>
                      <option value="weekly">Per Week</option>
                      <option value="monthly">Per Month</option>
                      <option value="yearly">Per Year</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="experience_level">Experience Level</label>
                  <select
                    id="experience_level"
                    name="experience_level"
                    value={formData.experience_level}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  >
                    <option value="">Select level</option>
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior Level</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Additional Info */}
          {currentStep === 3 && (
            <div className="form-step">
              <div className="step-content">
                <h3>Additional Information</h3>
                
                <div className="form-group">
                  <label htmlFor="skills_required">Required Skills</label>
                  <div className="skills-input-wrapper" style={{ position: 'relative' }}>
                    {skillsLoading ? (
                      <div style={{ padding: '12px', textAlign: 'center', color: '#6b7280' }}>
                        Loading available skills...
                      </div>
                    ) : (
                      <>
                        <input
                          ref={skillsInputRef}
                          type="text"
                          value={skillsInput}
                          onChange={handleSkillsInput}
                          onKeyDown={handleAddSkill}
                          onFocus={handleSkillsInputFocus}
                          placeholder="Search and select skills from available options"
                          disabled={isSubmitting}
                          className={errors.skills_required ? 'error' : ''}
                        />
                        {showSkillsDropdown && filteredSkills.length > 0 && (
                          <div 
                            ref={skillsDropdownRef}
                            className="skills-dropdown"
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: '#fff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                              maxHeight: '200px',
                              overflowY: 'auto',
                              zIndex: 1000,
                              marginTop: '4px'
                            }}
                          >
                            {filteredSkills.slice(0, 10).map((skill) => {
                              const skillName = skill.name || skill;
                              const isSelected = formData.skills_required.includes(skillName);
                              return (
                                <div
                                  key={skill.id || skillName}
                                  onClick={() => !isSelected && handleSkillSelect(skill)}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: isSelected ? 'not-allowed' : 'pointer',
                                    backgroundColor: isSelected ? '#f3f4f6' : '#fff',
                                    color: isSelected ? '#9ca3af' : '#111827',
                                    borderBottom: '1px solid #f3f4f6',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) {
                                      e.target.style.backgroundColor = '#f9fafb';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) {
                                      e.target.style.backgroundColor = '#fff';
                                    }
                                  }}
                                >
                                  <span>{skillName}</span>
                                  {isSelected && (
                                    <span style={{ color: '#10b981', fontSize: '14px' }}>✓</span>
                                  )}
                                </div>
                              );
                            })}
                            {filteredSkills.length > 10 && (
                              <div style={{ 
                                padding: '8px 12px', 
                                textAlign: 'center', 
                                color: '#6b7280', 
                                fontSize: '12px',
                                borderTop: '1px solid #f3f4f6'
                              }}>
                                Showing first 10 results. Type to filter more...
                              </div>
                            )}
                          </div>
                        )}
                        {showSkillsDropdown && filteredSkills.length === 0 && skillsInput.trim() && (
                          <div 
                            ref={skillsDropdownRef}
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: '#fff',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                              padding: '12px',
                              zIndex: 1000,
                              marginTop: '4px',
                              color: '#6b7280',
                              textAlign: 'center'
                            }}
                          >
                            No matching skills found. Please select from available options.
                          </div>
                        )}
                      </>
                    )}
                    <div className="skills-tags" style={{ marginTop: '8px' }}>
                      {formData.skills_required.map((skill, index) => (
                        <span key={index} className="skill-tag">
                          {skill}
                          <button
                            type="button"
                            onClick={() => removeSkill(skill)}
                            className="remove-skill"
                            disabled={isSubmitting}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    {errors.skills_required && (
                      <span className="error-text" style={{ display: 'block', marginTop: '4px' }}>
                        {errors.skills_required}
                      </span>
                    )}
                    <small style={{color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                      Select skills from the available system skills. Type to search.
                    </small>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="application_deadline">Application Deadline (Optional)</label>
                  <input
                    type="date"
                    id="application_deadline"
                    name="application_deadline"
                    value={formData.application_deadline}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    disabled={isSubmitting}
                  />
                </div>

                {companies && companies.length > 0 && (
                  <div className="form-group">
                    <label htmlFor="company_id">Post as Company (Optional)</label>
                    <select
                      id="company_id"
                      name="company_id"
                      value={formData.company_id || ''}
                      onChange={handleInputChange}
                      disabled={isSubmitting}
                    >
                      <option value="">Post as Individual</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Custom Questions Section */}
                <div className="form-group" style={{ marginTop: '32px', paddingTop: '32px', borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <label style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                      Application Questions (Optional)
                    </label>
                    <button
                      type="button"
                      onClick={addMetaField}
                      disabled={isSubmitting}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#667eea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <span>+</span> Add Question
                    </button>
                  </div>
                  <small style={{ color: '#6b7280', fontSize: '12px', display: 'block', marginBottom: '16px' }}>
                    Add custom questions that candidates will answer when applying. You can ask about their experience, availability, salary expectations, or any other relevant information.
                  </small>

                  {formData.meta_fields.map((metaField, index) => (
                    <div key={index} style={{
                      marginBottom: '24px',
                      padding: '20px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      backgroundColor: '#f9fafb'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                          Question {index + 1}
                        </h4>
                        <button
                          type="button"
                          onClick={() => removeMetaField(index)}
                          disabled={isSubmitting}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Remove
                        </button>
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>
                          Question Text <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input
                          type="text"
                          value={metaField.question}
                          onChange={(e) => updateMetaField(index, 'question', e.target.value)}
                          placeholder="e.g., Why are you interested in this position?"
                          disabled={isSubmitting}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>
                          How should candidates answer? <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <select
                          value={metaField.type}
                          onChange={(e) => updateMetaField(index, 'type', e.target.value)}
                          disabled={isSubmitting}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                        >
                          <option value="text">Short Answer (Single line text)</option>
                          <option value="textarea">Long Answer (Multiple lines text)</option>
                          <option value="rich_text">Formatted Answer (Rich text with formatting)</option>
                          <option value="dropdown">Choose One (Select from dropdown list)</option>
                          <option value="checkbox">Choose Multiple (Select multiple options)</option>
                          <option value="radio">Select One (Choose single option from list)</option>
                        </select>
                        <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          Select how candidates should provide their answer to this question
                        </small>
                      </div>

                      {(metaField.type === 'dropdown' || metaField.type === 'checkbox' || metaField.type === 'radio') && (
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500' }}>
                              Answer Choices <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => addMetaFieldOption(index)}
                              disabled={isSubmitting}
                              style={{
                                padding: '4px 12px',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              + Add Choice
                            </button>
                          </div>
                          {metaField.options.map((option, optIndex) => (
                            <div key={optIndex} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => updateMetaFieldOption(index, optIndex, e.target.value)}
                                placeholder={`Choice ${optIndex + 1} (e.g., Yes, No, Maybe)`}
                                disabled={isSubmitting}
                                style={{
                                  flex: 1,
                                  padding: '8px 12px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  fontSize: '14px'
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => removeMetaFieldOption(index, optIndex)}
                                disabled={isSubmitting || metaField.options.length === 1}
                                style={{
                                  padding: '8px 12px',
                                  backgroundColor: '#ef4444',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id={`meta_required_${index}`}
                          checked={metaField.required}
                          onChange={(e) => updateMetaField(index, 'required', e.target.checked)}
                          disabled={isSubmitting}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <label htmlFor={`meta_required_${index}`} style={{ fontSize: '13px', cursor: 'pointer', margin: 0 }}>
                          Required field
                        </label>
                      </div>
                    </div>
                  ))}

                  {formData.meta_fields.length === 0 && (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      border: '2px dashed #d1d5db',
                      borderRadius: '8px',
                      color: '#6b7280'
                    }}>
                      <p style={{ margin: 0 }}>No custom questions added yet.</p>
                      <p style={{ margin: '8px 0 0 0', fontSize: '13px' }}>Click "Add Question" to create application questions.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="form-actions">
            {currentStep > 1 && (
              <button
                type="button"
                className="btn-secondary"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Back
              </button>
            )}
            <div className="actions-right">
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={isSubmitting}
                >
                  Next
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn-secondary btn-save-draft"
                    onClick={handleSaveDraft}
                    disabled={isSavingDraft || isPublishing}
                  >
                    {isSavingDraft ? (
                      <>
                        <span className="spinner"></span>
                        Saving...
                      </>
                    ) : (
                      'Save as Draft'
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn-primary btn-publish"
                    onClick={handlePublish}
                    disabled={isSavingDraft || isPublishing}
                  >
                    {isPublishing ? (
                      <>
                        <span className="spinner"></span>
                        {jobToEdit ? 'Updating...' : 'Publishing...'}
                      </>
                    ) : (
                      jobToEdit ? 'Update Job' : 'Publish Job'
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default JobPostingForm;

